import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import { envConfig } from '../config/environment';

class WebSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private get canUseNotifications(): boolean {
    return typeof window !== 'undefined' && typeof (window as any).Notification !== 'undefined';
  }

  connect(userId: string) {
    if (this.socket?.connected) {
      console.log('🔌 WebSocket already connected');
      return;
    }

    // Clean up any existing socket before creating a new one
    if (this.socket) {
      this.socket.removeAllListeners();
      if (this.socket.connected) {
        this.socket.disconnect();
      }
      this.socket = null;
    }

    // Use same origin for relative API paths (staging/production behind IIS proxy)
    const serverUrl = envConfig.apiUrl.startsWith('/')
      ? window.location.origin
      : envConfig.apiUrl.replace(/\/api$/, '');
    console.log('🔌 Connecting to WebSocket server:', serverUrl);

    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket?.id);
      this.isConnected = true;
      
      // Authenticate with user ID
      this.socket?.emit('authenticate', userId);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
      this.isConnected = false;
    });

    this.socket.on('notification', (notification) => {
      console.log('📱 Received notification:', notification);
      this.handleNotification(notification);
    });

    this.socket.on('connect_error', (error) => {
      // Only log error if we're not in the process of disconnecting
      if (this.socket && !this.socket.disconnected) {
        console.error('❌ WebSocket connection error:', error);
      }
    });
  }

  disconnect() {
    if (this.socket) {
      // Only disconnect if socket is actually connected
      // This prevents the "closed before connection established" error
      if (this.socket.connected) {
        console.log('🔌 Disconnecting WebSocket');
        this.socket.disconnect();
      } else {
        console.log('🔌 WebSocket not connected, removing reference');
      }
      this.socket.removeAllListeners();
      this.socket = null;
      this.isConnected = false;
    }
  }

  private handleNotification(notification: any) {
    const { type, title, message, data } = notification;

    switch (type) {
      case 'missing_employee_alert':
        this.showMissingEmployeeAlert(title, message, data);
        break;
      default:
        console.log('📱 Unknown notification type:', type);
    }
  }

  private showMissingEmployeeAlert(title: string, message: string, data: any) {
    // Format notification as plain text (react-toastify renders plain text correctly)
    const timeText = data.minutesLate 
      ? `${data.minutesLate} ${data.minutesLate === 1 ? 'minute' : 'minutes'} late`
      : '';
    
    // Create a multi-line message without HTML
    const notificationMessage = timeText 
      ? `${title}\n${message}\n${timeText}`
      : `${title}\n${message}`;
    
    toast.error(notificationMessage, {
      position: 'top-right',
      autoClose: 20000, // 20 seconds (increased from 10 seconds)
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: {
        backgroundColor: '#ffebee',
        border: '1px solid #f44336',
        color: '#d32f2f',
        whiteSpace: 'pre-line', // Preserve line breaks
        fontWeight: 'normal'
      }
    });

    // Also show browser notification if supported and permission granted
    if (this.canUseNotifications) {
      const N = (window as any).Notification as Notification;
      if ((N as any).permission === 'granted') {
        // Safari iOS may not support constructor without service worker; wrap in try/catch
        try {
          // @ts-ignore - constructor presence varies across browsers
          new (window as any).Notification(title, {
            body: message,
            icon: '/favicon.png',
            tag: `missing-employee-${data.employeeId}`
          });
        } catch (_) {
          // Silently ignore if not supported
        }
      }
    }
  }

  // Request browser notification permission
  requestNotificationPermission() {
    if (!this.canUseNotifications) return;
    const N = (window as any).Notification as Notification;
    if ((N as any).permission === 'default') {
      // @ts-ignore - requestPermission signature differs
      (N as any).requestPermission?.().then((permission: string) => {
        if (permission === 'granted') {
          console.log('✅ Browser notifications enabled');
        } else {
          console.log('❌ Browser notifications denied');
        }
      }).catch(() => {});
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

export default new WebSocketService();
